import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { featureFlagsClient } from 'services/connectivity/feature-flags/client';
import { FeatureFlagsResponse } from 'services/connectivity/feature-flags/types';
import { getAccessToken } from 'helpers/account';
import { errorDeserialize } from 'helpers/error-deserializer';

export enum FeatureNames {
  TestToggle = 'test_toggle',
  SamlConfiguration = 'saml-configuration',
  CanUpgradeSubscription = 'can_upgrade_subscription',
  TextPlatformAsProduct = 'text-platfom-as-product',
}

export type Features = Record<FeatureNames, boolean>;

export const defaultFeatures: Features = {
  [FeatureNames.TestToggle]: false,
  [FeatureNames.SamlConfiguration]: false,
  [FeatureNames.CanUpgradeSubscription]: false,
  [FeatureNames.TextPlatformAsProduct]: false,
};

export function deserializeFeatures(features: FeatureFlagsResponse): Features {
  return Object.values(features.feature_flags).reduce(
    (acc, feature) => ({
      ...acc,
      [feature.name]: feature.value,
    }),
    {} as Features
  );
}

interface IUseFetchFeatures {
  data?: Features;
  isFetching: boolean;
}

export async function fetchFeatures(token: string): Promise<Features> {
  const { result, error } = await featureFlagsClient.get(token);

  if (error) {
    throw errorDeserialize(error);
  }

  return deserializeFeatures(result);
}

export function useFetchFeatures(): IUseFetchFeatures {
  const { data, isFetching, isFetched, refetch } = useQuery({
    queryKey: ['features-control'],
    queryFn: () => fetchFeatures(getAccessToken()),
    retry: 1,
    initialData: defaultFeatures,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!isFetched) {
      refetch();
    }
  }, [isFetched]);

  return { data, isFetching };
}
